import * as Sentry from '@sentry/browser';
import { unformat } from 'accounting';
import convertObjectPropsToCamelCase from '~/javascripts/lib/convert_object_props_to_camel_case';
import parseBoolean from '~/javascripts/lib/parse_boolean';
import tryJsonParse from '~/javascripts/lib/try_json_parse';
import { GENERAL_ERROR_MESSAGE, DEFAULT_ERROR_CODE } from '~/javascripts/constants';
import { extractErrorCode } from '~/javascripts/lib/error_utils';

function formatErrorResponse(message, code = DEFAULT_ERROR_CODE, attributes = []) {
  const cause = extractErrorCode(code, message);

  return {
    attributes,
    message,
    code,
    cause,
  };
}

/**
 * Get user friendly error message by priority
 * @param {Object} response - Response directly from Zuora or via backend middleware
 * @return {Object} contains `code` and `message` keys
 */
export function getPaymentErrorFromResponse(response) {
  const errors = response?.payload?.errors || response?.errors;

  if (response?.gateway_response) {
    return formatErrorResponse(response.gateway_response);
  }

  if (errors?.message) {
    return formatErrorResponse(errors.message, errors.code, errors.attributes);
  }

  Sentry.setExtra('malformed error response', response);
  Sentry.captureException(new Error('malformed error'));

  return formatErrorResponse(GENERAL_ERROR_MESSAGE, errors?.code, errors?.attributes);
}

/**
 * Invoice data is not uniform from the backend. Use this to smooth it over
 * @param {Object} data - Response directly from Zuora or via backend middleware
 */
export function getSubscriptionCreateOrUpdatePreviewResponse(data) {
  const invoiceData = data?.invoice_data;

  if (invoiceData) {
    return invoiceData;
  }

  Sentry.setExtra('malformed error response', data);

  throw new Error('Malformed subscriptionResponse');
}

export function getSubscriptionUpgradeResponse(data) {
  const {
    amount_without_tax: subtotalWithoutTax,
    plan_name: planName,
    seats,
    rebate,
    tax,
    total,
    upgrade_charge: upgradeSubtotal,
  } = data;

  return {
    newPlanName: planName,
    quantity: seats,
    rebate,
    tax,
    total,
    upgradeSubtotal,
    subtotalWithoutTax,
  };
}

export function parseAndConvertSubscriptionJson(subscriptionJson) {
  const subscription = tryJsonParse(subscriptionJson);

  return convertObjectPropsToCamelCase(subscription, {
    deep: true,
  });
}

export function parseSubscriptionRenewData(rawData) {
  const {
    isCloudLicense,
    subscriptionsPath,
    subscription: subscriptionJson,
    availablePlans: availablePlansRaw,
    hasCustomerBillingAccounts,
    zuoraAccountNumber,
  } = rawData;

  const availablePlans = tryJsonParse(availablePlansRaw, []).map((plan) =>
    convertObjectPropsToCamelCase(plan),
  );

  if (!availablePlans?.length) {
    Sentry.captureException(new Error('Could not get availablePlans from the backend'));
    return null;
  }

  const subscription = parseAndConvertSubscriptionJson(subscriptionJson);
  if (!subscription) {
    return null;
  }

  return {
    subscriptionsPath,
    trueupsRenewalTerm: subscription.trueupsRenewalTerm,
    isCloudLicense: parseBoolean(isCloudLicense),
    availablePlans,
    trueupsCount: Number(subscription.trueupsCount),
    minUserCount: Number(subscription.minUserCount),
    trueupPrice: Number(unformat(subscription.trueupPrice)),
    subscription,
    hasUnpaidOverage: parseBoolean(subscription.hasUnpaidOverages),
    q3ReconciliationSuccessful: parseBoolean(subscription.q3ReconciliationSuccessful),
    hasCustomerBillingAccounts: parseBoolean(hasCustomerBillingAccounts),
    zuoraAccountNumber,
  };
}
